import React, {
  FC,
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from 'react';

import { BigNumber } from 'ethers';

import { useTrackedState } from '@context/store';

import { getFarmListForProvider } from '@views/Farms/helpers/getFarmListForProvider';

import { useFarmsData } from '@hooks/useFarmsData';

const fallbackFarm = getFarmListForProvider('bitcoincom_eth')[0];

type FarmContextState = {
  farmPairs: IFarmPair[];
  farmBalances: Record<string, BigNumber>;
  farmTotalSupply: Record<string, BigNumber>;
  farmEarned: Record<string, BigNumber>;
  selectedFarm: IFarmPair;
};

const DEFAULT_STATE: FarmContextState = {
  farmPairs: [],
  farmBalances: {},
  farmEarned: {},
  farmTotalSupply: {},
  selectedFarm: fallbackFarm,
};

const FarmsContext = createContext<FarmContextState>(DEFAULT_STATE);

export const FarmsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { selectedFarmAddress } = useTrackedState();

  // Get farms data
  const { farmPairs, farmBalances, farmEarned, farmTotalSupply } =
    useFarmsData();

  // Farm selection logic
  const selectedFarm = useMemo(() => {
    const farmByAddress =
      farmPairs.find(
        f =>
          f.contractAddress.toLowerCase() === selectedFarmAddress.toLowerCase(),
      ) ||
      getFarmListForProvider('bitcoincom_eth').find(
        f =>
          f.contractAddress.toLowerCase() === selectedFarmAddress.toLowerCase(),
      );

    return farmByAddress || fallbackFarm;
  }, [selectedFarmAddress, farmPairs]);

  const value = useMemo(
    () => ({
      farmPairs,
      farmBalances,
      farmEarned,
      farmTotalSupply,
      selectedFarm,
    }),
    [farmPairs, farmBalances, farmEarned, farmTotalSupply, selectedFarm],
  );

  return (
    <FarmsContext.Provider value={value}>{children}</FarmsContext.Provider>
  );
};

export const useFarms = () => useContext(FarmsContext);

export default FarmsProvider;
